import React from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Stack,
  Button,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import { Link as RouterLink } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Booking } from "../../../../types/Booking";
import { joinBookingCallFn } from "../../../../services/api/booking.api";
import { enqueueSnackbar } from "notistack";
dayjs.extend(customParseFormat);

interface Props {
  id: string;
  title: string;
  talent: string;
  avatar: string;
  date: string;
  cardId?: string;
  timeRange: string;
  channel: string;
  cardName: string;
  filter?: string;
  status?: string;
  bookingId: number;
}

const SessionCard: React.FC<Props> = ({
  title,
  talent,
  avatar,
  date,
  timeRange,
  channel,
  filter,
  status,
  bookingId,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const hasChannel = Boolean(channel);

  // —— Netflix-ish tokens
  const COLORS = {
    panel: "#141414",
    panelHover: "#191919",
    border: "rgba(255,255,255,0.12)",
    text: "#ffffff",
    textMuted: "rgba(255,255,255,0.75)",
    red: "#E50914",
    redHover: "#b20710",
    soft: "rgba(255,255,255,0.06)",
  };

  const now = dayjs();
  const [startTimeStr, endTimeStr] = timeRange.split(" - ");
  const start = dayjs(`${date} ${startTimeStr}`, "DD MMM YYYY hh:mm A");
  const end = dayjs(`${date} ${endTimeStr}`, "DD MMM YYYY hh:mm A");

  const isPast = end.isBefore(now);
  const isOngoing = status === "Ongoing";

  const handleJoinBooking = async (bookingId: number) => {
    try {
      const videoTokenObject = await joinBookingCallFn(bookingId);

      localStorage.setItem(
        "spadeVideoToken",
        videoTokenObject?.data?.data?.token
      );
      localStorage.setItem(
        "spadeAgoraUid",
        videoTokenObject.data.data.agora_uid
      );

      // navigate(`/coffee-call/${data?.channel_name}`);
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to join coffee call",
        {
          variant: "error",
        }
      );
    }
  };

  // Chip style helper (keeps logic, updates visuals)
  const chipStyle = (bg = COLORS.soft, border = COLORS.border, color = COLORS.text) => ({
    fontWeight: 800,
    fontSize: 12,
    px: 1.25,
    borderRadius: 2,
    backgroundColor: bg,
    color,
    border: `1px solid ${border}`,
    letterSpacing: 0.2,
  });

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2.25 },
        borderRadius: 3,
        display: "flex",
        flexDirection: isSmall ? "column" : "row",
        alignItems: isSmall ? "flex-start" : "center",
        justifyContent: "space-between",
        gap: { xs: 1.5, sm: 2.5 },
        background: `linear-gradient(180deg, rgba(20,20,20,0.98), rgba(20,20,20,0.92))`,
        border: `1px solid ${COLORS.border}`,
        color: COLORS.text,
        boxShadow: "0 18px 40px rgba(0,0,0,0.45)",
        transition: "transform 200ms ease, box-shadow 220ms ease, background 220ms ease",
        "&:hover": {
          background: COLORS.panelHover,
          transform: "translateY(-2px)",
          boxShadow: "0 26px 60px rgba(0,0,0,0.55)",
        },
      }}
    >
      {/* Left: Avatar & Info */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flex={1}
        minWidth={0}
      >
        <Avatar
          variant="rounded"
          sx={{
            width: 56,
            height: 56,
            fontSize: 18,
            borderRadius: 2,
            bgcolor: COLORS.soft,
            border: `1px solid ${COLORS.border}`,
            backgroundImage: avatar?.startsWith("http")
              ? "none"
              : `linear-gradient(135deg, ${COLORS.red} 0%, ${COLORS.redHover} 100%)`,
            boxShadow: avatar?.startsWith("http")
              ? "0 6px 18px rgba(0,0,0,0.45)"
              : "0 10px 24px rgba(229,9,20,0.25)",
            userSelect: "none",
          }}
          src={avatar?.startsWith("http") ? avatar : undefined}
        >
          {!avatar?.startsWith("http") && (talent?.[0] || "T")}
        </Avatar>

        <Box minWidth={0}>
          <Typography
            fontWeight={800}
            fontSize={{ xs: 15, sm: 17 }}
            fontFamily="Lexend"
            noWrap
            title={title}
            sx={{ mb: 0.25, color: COLORS.text, letterSpacing: "-0.015em" }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            noWrap
            title={`With: ${talent}`}
            sx={{ lineHeight: 1.6, color: COLORS.textMuted, fontFamily: "Lexend" }}
          >
            With <Box component="span" sx={{ color: COLORS.text, fontWeight: 700 }}>{talent}</Box>
          </Typography>

          <Typography
            variant="body2"
            noWrap
            sx={{ opacity: 0.9, color: COLORS.textMuted, fontFamily: "Lexend" }}
          >
            {date} &nbsp;|&nbsp; {timeRange}
          </Typography>
        </Box>
      </Stack>

      {/* Middle: Status */}
      <Box
        sx={{
          minWidth: 110,
          display: "flex",
          justifyContent: isSmall ? "flex-start" : "center",
          mt: isSmall ? 0.5 : 0,
        }}
      >
        {filter === "requested" ? (
          <Chip label="Pending" sx={chipStyle("rgba(255,193,7,0.12)", "rgba(255,193,7,0.35)", "#FFCA28")} />
        ) : isOngoing ? (
          <Chip label="Ongoing" sx={chipStyle("rgba(76,175,80,0.14)", "rgba(76,175,80,0.35)", "#81C784")} />
        ) : filter === "cancelled" ? (
          <Chip label="Cancelled" sx={chipStyle("rgba(239,68,68,0.14)", "rgba(239,68,68,0.35)", "#F87171")} />
        ) : filter === "upcoming" ? (
          <Chip label="Upcoming" sx={chipStyle("rgba(59,130,246,0.14)", "rgba(59,130,246,0.35)", "#93C5FD")} />
        ) : (
          isPast && (
            <Chip label="Completed" sx={chipStyle("rgba(156,163,175,0.14)", "rgba(156,163,175,0.35)", "#E5E7EB")} />
          )
        )}
      </Box>

      {/* Right: Join Button */}
      {(filter === "today" || status === "Ongoing") && (
        <Box mt={isSmall ? 1 : 0} minWidth={110} display="flex" justifyContent={"flex-end"}>
          <Button
            component={RouterLink}
            to={hasChannel ? channel : "#"}
            startIcon={<VideoCallIcon />}
            variant="contained"
            onClick={() => handleJoinBooking(Number(bookingId))}
            sx={{
              borderRadius: 2,
              background: `linear-gradient(180deg, ${COLORS.red}, ${COLORS.redHover})`,
              color: "#fff",
              px: { xs: 1.5, sm: 2.25 },
              py: { xs: 1, sm: 1.25 },
              textTransform: "none",
              fontFamily: "Lexend",
              fontWeight: 800,
              letterSpacing: 0.25,
              boxShadow: "0 12px 28px rgba(229,9,20,0.35)",
              "&:hover": {
                background: COLORS.redHover,
                boxShadow: "0 16px 36px rgba(229,9,20,0.45)",
              },
              "&:disabled": {
                background: "linear-gradient(180deg, #3a3a3a, #2a2a2a)",
                color: "rgba(255,255,255,0.65)",
                cursor: "not-allowed",
              },
            }}
            aria-label="Join session"
          >
            Join
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default SessionCard;
